import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../AuthContext";
import { useI18n } from "../i18n";
import { Header } from "../components/Frumy";
import { FeedbackForm } from "../components/Feedback";
import { ArrowLeft, Trash, Plus, Sparkle, CheckCircle, Sparkle as SparkAI, FilePdf, FileCsv, WarningCircle, CalendarBlank, Info, ArrowCounterClockwise, CaretDown, CircleNotch, ChatCircleDots } from "@phosphor-icons/react";

export default function Preview() {
  const { t } = useI18n();
  const nav = useNavigate();
  const addFileRef = useRef();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ files: [], dedup_removed: 0 });
  const [removed, setRemoved] = useState([]);
  const [showRemoved, setShowRemoved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addingFile, setAddingFile] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("frumy_extracted_tx");
    const m = sessionStorage.getItem("frumy_extract_meta");
    const rem = sessionStorage.getItem("frumy_extract_removed");
    if (!raw) { nav("/upload"); return; }
    const tx = JSON.parse(raw).map((x, i) => ({
      id: i,
      date: x.date || "",
      description: x.description || "",
      amount: Number(x.amount || 0), // keep original sign (expense negative, income positive)
      raw: x.raw || "",
      dkey: x.dkey || "",
    }));
    setRows(tx);
    if (m) setMeta(JSON.parse(m));
    if (rem) setRemoved(JSON.parse(rem));
  }, [nav]);

  const total = useMemo(() => rows.reduce((s, r) => s + (Number(r.amount) || 0), 0), [rows]);

  const monthsCovered = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => { if (r.date && r.date.length >= 7) s.add(r.date.slice(0, 7)); });
    return s.size;
  }, [rows]);

  const update = (id, field, value) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  const removeRow = (id) => setRows((rs) => rs.filter((r) => r.id !== id));
  const addRow = () => setRows((rs) => [{ id: Date.now(), date: "", description: "", amount: 0 }, ...rs]);

  const restore = (idx) => {
    const item = removed[idx];
    if (!item) return;
    setRows((rs) => [{ id: Date.now() + Math.random(), date: item.date, description: item.description, amount: Number(item.amount) || 0 }, ...rs]);
    setRemoved((prev) => prev.filter((_, i) => i !== idx));
  };

  const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n || 0));

  const errorMessage = (code) => {
    const map = {
      INVALID_CSV: t("errInvalidCsv"), NO_COLUMNS: t("errNoColumns"),
      NO_EXPENSES: t("errNoExpenses"), PDF_UNREADABLE: t("errPdfUnreadable"),
    };
    return map[code] || t("errGeneric");
  };

  const addFile = async (file) => {
    if (!file) return;
    const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
    setAddingFile(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/${isPdf ? "pdf" : "csv"}/extract`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const extracted = data.transactions || [];
      const hasBal = !!data.has_balance;
      const dkeyOf = (tx, line) => {
        const raw = tx.raw || `${tx.date}|${String(tx.description || "").toLowerCase()}|${Number(tx.amount).toFixed(2)}`;
        return hasBal ? raw : `${raw}#${line}`;
      };

      // Balance-aware dedup of the new file against already-loaded rows.
      const existing = new Set(rows.map((r) => r.dkey).filter(Boolean));
      const newKept = [];
      const newRemoved = [];
      extracted.forEach((tx, j) => {
        const k = dkeyOf(tx, j);
        if (existing.has(k)) newRemoved.push({ date: tx.date, description: tx.description, amount: tx.amount });
        else { existing.add(k); newKept.push({ ...tx, _dkey: k }); }
      });

      const base = Date.now();
      setRows((rs) => [
        ...newKept.map((tx, i) => ({ id: base + i, date: tx.date, description: tx.description, amount: Number(tx.amount) || 0, raw: tx.raw || "", dkey: tx._dkey })),
        ...rs,
      ]);
      if (newRemoved.length) setRemoved((prevR) => [...prevR, ...newRemoved]);
      setMeta((m) => ({
        files: [...(m.files || []), { file: file.name, method: data.method, layout_name: data.layout_name, count: data.count || extracted.length }],
        dedup_removed: (m.dedup_removed || 0) + newRemoved.length,
      }));
      toast.success(`${file.name}: +${newKept.length}`);
    } catch (e) {
      toast.error(`${file.name}: ${errorMessage(e.response?.data?.detail)}`);
    } finally {
      setAddingFile(false);
    }
  };

  const analyze = async () => {
    setLoading(true);
    try {
      const transactions = rows
        .filter((r) => r.date && r.description)
        .map((r) => ({ date: r.date, description: r.description, amount: Number(r.amount) || 0 }));
      const { data } = await axios.post(`${API}/analyze-transactions`, { transactions });
      sessionStorage.setItem("frumy_analysis", JSON.stringify(data));
      sessionStorage.removeItem("frumy_extracted_tx");
      sessionStorage.removeItem("frumy_extract_meta");
      nav("/conferma");
    } catch (e) {
      const code = e.response?.data?.detail;
      toast.error(code === "NO_EXPENSES" ? t("errNoExpenses") : t("errGeneric"));
    } finally { setLoading(false); }
  };

  const files = meta.files || [];
  const fbFormat = files.some((f) => /\.pdf$/i.test(f.file || "")) ? "PDF" : (files.length ? "CSV" : "");
  const lowResult = files.length > 0 && rows.length > 0 && rows.length < 3;
  useEffect(() => { if (lowResult) setShowFeedback(true); }, [lowResult]);

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <button data-testid="preview-back-btn" onClick={() => nav("/upload")}
            className="pill-btn inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold border border-[var(--frumy-border)] hover:bg-[var(--frumy-muted)] mb-4">
            <ArrowLeft size={16} aria-hidden />{t("back")}
          </button>

          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--heading)]">{t("pdfPreviewTitle")}</h1>
          <p className="text-sm text-[var(--text-muted)] mt-2 max-w-xl">{t("pdfPreviewSub")}</p>

          {/* Per-file processing summary */}
          {files.length > 0 && (
            <div data-testid="files-summary" className="mt-5 space-y-2">
              <p className="text-xs tracking-[0.12em] uppercase font-bold text-[var(--text-muted)]">{t("filesProcessedTitle")}</p>
              <div className="flex flex-wrap gap-2">
                {files.map((f, i) => {
                  const isPdf = /\.pdf$/i.test(f.file);
                  const isNew = f.method === "ai_new";
                  if (f.error) {
                    return (
                      <span key={i} data-testid="file-chip-error"
                        className="inline-flex items-center gap-1.5 pill-btn px-3 py-1.5 text-xs font-semibold bg-red-50 text-red-600 border border-red-200">
                        <WarningCircle weight="fill" size={14} aria-hidden />{f.file} — {f.error}
                      </span>
                    );
                  }
                  return (
                    <span key={i} data-testid="file-chip"
                      className="inline-flex items-center gap-1.5 pill-btn px-3 py-1.5 text-xs font-semibold bg-[var(--frumy-muted)] text-[var(--heading)]">
                      {isPdf ? <FilePdf weight="fill" size={14} className="text-[var(--frumy-green)]" aria-hidden /> : <FileCsv weight="fill" size={14} className="text-[var(--frumy-green)]" aria-hidden />}
                      {f.file}
                      {isPdf && (isNew ? <SparkAI weight="fill" size={12} className="text-[var(--frumy-green)]" aria-hidden title="AI" /> : <CheckCircle weight="fill" size={12} className="text-[var(--frumy-green)]" aria-hidden />)}
                      <span className="text-[var(--text-muted)]">· {t("fileTxCount", { count: f.count || 0 })}</span>
                    </span>
                  );
                })}
              </div>
              {removed.length > 0 && (
                <div data-testid="removed-panel">
                  <button data-testid="dedup-note" onClick={() => setShowRemoved((v) => !v)}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[var(--frumy-lock)] hover:underline">
                    <CaretDown size={13} weight="bold" className={`transition-transform ${showRemoved ? "rotate-180" : ""}`} aria-hidden />
                    {t("dedupRemoved", { n: removed.length })} · {t("viewRemoved")}
                  </button>
                  {showRemoved && (
                    <div data-testid="removed-list" className="mt-2 frumy-card divide-y divide-[var(--frumy-border)] overflow-hidden">
                      <p className="px-4 py-2 text-xs tracking-[0.12em] uppercase font-bold text-[var(--text-muted)]">{t("removedTitle")}</p>
                      {removed.map((r, i) => (
                        <div key={i} data-testid="removed-row" className="flex items-center justify-between gap-3 px-4 py-2">
                          <span className="flex items-center gap-3 text-sm min-w-0">
                            <span className="text-[var(--text-muted)] shrink-0">{r.date}</span>
                            <span className="text-[var(--heading)] truncate">{r.description}</span>
                            <span className="num-hero text-[var(--text-muted)] shrink-0">{eur(r.amount)}</span>
                          </span>
                          <button data-testid="restore-btn" onClick={() => restore(i)}
                            className="pill-btn inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold border border-[var(--frumy-border)] hover:bg-[var(--frumy-muted)] shrink-0">
                            <ArrowCounterClockwise size={13} aria-hidden />{t("restore")}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="mt-6 mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <span data-testid="pdf-count" className="flex items-center gap-2 text-sm font-bold text-[var(--heading)]">
                <FilePdf size={18} className="text-[var(--frumy-green)]" aria-hidden />
                {t("pdfDetectedCount", { n: rows.length })}
              </span>
              {monthsCovered > 0 && (
                <span data-testid="coverage-badge"
                  className={`inline-flex items-center gap-1.5 pill-btn px-3 py-1.5 text-xs font-bold ${monthsCovered >= 2 ? "bg-emerald-50 text-[var(--frumy-green)] border border-emerald-200" : "bg-amber-50 text-[var(--frumy-lock)] border border-amber-200"}`}>
                  <CalendarBlank weight="fill" size={13} aria-hidden />
                  {monthsCovered === 1 ? t("coverageOne") : t("coverageMonths", { n: monthsCovered })}
                </span>
              )}
            </div>
            <input ref={addFileRef} data-testid="add-file-input" type="file" accept=".csv,.pdf,text/csv,application/pdf" hidden
              onChange={(e) => { addFile(e.target.files?.[0]); e.target.value = ""; }} />
            <div className="flex flex-col sm:flex-row gap-2">
              <button data-testid="add-file-btn" onClick={() => addFileRef.current?.click()} disabled={addingFile}
                className="pill-btn cta-metal w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-3 py-2 sm:py-1.5 text-sm font-bold text-white disabled:opacity-60">
                {addingFile ? <CircleNotch size={15} className="animate-spin" aria-hidden /> : <Plus size={15} aria-hidden />}
                {t("addFileBtn")}
              </button>
              <button data-testid="add-row-btn" onClick={addRow}
                className="pill-btn w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-3 py-2 sm:py-1.5 text-sm font-semibold border border-[var(--frumy-border)] hover:bg-[var(--frumy-muted)]">
                <Plus size={15} aria-hidden />{t("addRow")}
              </button>
            </div>
          </div>

          {monthsCovered > 0 && monthsCovered < 2 && (
            <div data-testid="coverage-hint" className="flex items-start gap-2.5 mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <Info weight="fill" size={18} className="text-[var(--frumy-lock)] shrink-0 mt-0.5" aria-hidden />
              <p className="text-sm leading-relaxed text-amber-900">{t("coverageHint")}</p>
            </div>
          )}

          <div className="frumy-card overflow-hidden hidden sm:block">
            <div className="grid grid-cols-[130px_1fr_120px_44px] gap-2 px-4 py-2.5 text-xs tracking-[0.12em] uppercase font-bold text-[var(--text-muted)] border-b border-[var(--frumy-border)]">
              <span>{t("colDate")}</span><span>{t("colDesc")}</span><span className="text-right">{t("colAmount")}</span><span />
            </div>
            <div className="max-h-[52vh] overflow-y-auto divide-y divide-[var(--frumy-border)]">
              {rows.length === 0 && <p className="p-6 text-sm text-[var(--text-muted)]">{t("noPreviewData")}</p>}
              {rows.map((r) => (
                <div key={r.id} data-testid="tx-row" className="grid grid-cols-[130px_1fr_120px_44px] gap-2 px-4 py-2 items-center">
                  <input data-testid="tx-date" type="date" value={r.date} onChange={(e) => update(r.id, "date", e.target.value)}
                    className="rounded-md border border-[var(--frumy-border)] bg-transparent px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[var(--brand-to)]" />
                  <input data-testid="tx-desc" value={r.description} onChange={(e) => update(r.id, "description", e.target.value)}
                    className="rounded-md border border-[var(--frumy-border)] bg-transparent px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--brand-to)]" />
                  <input data-testid="tx-amount" type="number" step="0.01" value={r.amount} onChange={(e) => update(r.id, "amount", e.target.value)}
                    className="rounded-md border border-[var(--frumy-border)] bg-transparent px-2 py-1.5 text-sm text-right num-hero focus:outline-none focus:ring-1 focus:ring-[var(--brand-to)]" />
                  <button data-testid="tx-remove" onClick={() => removeRow(r.id)} title={t("remove")}
                    className="grid place-items-center h-8 w-8 rounded-md text-[var(--text-muted)] hover:bg-red-50 hover:text-red-600 transition-colors">
                    <Trash size={16} aria-hidden />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile: editable transaction cards */}
          <div className="sm:hidden space-y-3">
            {rows.length === 0 && <p className="frumy-card p-6 text-sm text-[var(--text-muted)]">{t("noPreviewData")}</p>}
            {rows.map((r) => (
              <div key={r.id} data-testid="tx-card" className="frumy-card p-3.5">
                <div className="flex items-center gap-2">
                  <input data-testid="tx-date-m" type="date" value={r.date} onChange={(e) => update(r.id, "date", e.target.value)}
                    className="rounded-md border border-[var(--frumy-border)] bg-transparent px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[var(--brand-to)]" />
                  <button data-testid="tx-remove-m" onClick={() => removeRow(r.id)} title={t("remove")}
                    className="ml-auto grid place-items-center h-8 w-8 rounded-md text-[var(--text-muted)] hover:bg-red-50 hover:text-red-600 transition-colors shrink-0">
                    <Trash size={16} aria-hidden />
                  </button>
                </div>
                <input data-testid="tx-desc-m" value={r.description} onChange={(e) => update(r.id, "description", e.target.value)}
                  placeholder={t("colDesc")}
                  className="mt-2 w-full rounded-md border border-[var(--frumy-border)] bg-transparent px-2.5 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--brand-to)]" />
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">{t("colAmount")}</span>
                  <input data-testid="tx-amount-m" type="number" step="0.01" value={r.amount} onChange={(e) => update(r.id, "amount", e.target.value)}
                    className="ml-auto w-36 rounded-md border border-[var(--frumy-border)] bg-transparent px-2 py-2 text-base text-right num-hero font-bold focus:outline-none focus:ring-1 focus:ring-[var(--brand-to)]" />
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
            <span className="text-sm text-[var(--text-muted)]">
              {t("total")}: <b className="text-[var(--heading)] num-hero">
                {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Math.abs(total))}
              </b>
            </span>
            <button data-testid="analyze-tx-btn" onClick={analyze} disabled={loading || rows.length === 0}
              className="pill-btn cta-metal inline-flex items-center gap-2 text-white font-bold px-7 py-3.5 disabled:opacity-60">
              <Sparkle weight="fill" size={18} aria-hidden />
              {loading ? t("analyzing") : t("proceedAnalysis")}
            </button>
          </div>

          {lowResult && (
            <div data-testid="low-result-hint" className="flex items-start gap-2.5 mt-6 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <WarningCircle weight="fill" size={18} className="text-[var(--frumy-lock)] shrink-0 mt-0.5" aria-hidden />
              <p className="text-sm leading-relaxed text-amber-900">{t("fbLowResult")}</p>
            </div>
          )}

          <div className="mt-6">
            <button data-testid="result-wrong-link" onClick={() => setShowFeedback((v) => !v)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--frumy-green)] hover:underline">
              <ChatCircleDots size={15} aria-hidden />{t("fbResultWrong")}
            </button>
            {showFeedback && (
              <FeedbackForm type="parsing" defaultFormat={fbFormat}
                onClose={() => setShowFeedback(false)} onSent={() => setShowFeedback(false)} />
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
